import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { WindowService } from '../shared/services/window.service';
import { isFirefoxSecurityError } from './is-firefox-security-error.utility';
import { SERVICE_WORKER_API_AVAILABLE } from './service-worker-api-available.injection-token';

export const enum ServiceWorkerState {
    Controlled = 'Controlled',
    SupportedNotControlled = 'SupportedNotControlled',
    Unknown = 'Unknown',
    Unsupported = 'Unsupported'
}

@Injectable({
    providedIn: 'root'
})
export class ServiceWorkerStateService {
    private stateSubject: BehaviorSubject<ServiceWorkerState>;
    private currentServiceWorkerState: ServiceWorkerState;

    constructor(
        @Inject(SERVICE_WORKER_API_AVAILABLE) readonly hasServiceWorkerApi: boolean,
        readonly windowService: WindowService,
        readonly globalErrorHandler: OfficeHomePageErrorHandler
    ) {
        if (!hasServiceWorkerApi) {
            this.updateCurrentState(ServiceWorkerState.Unsupported);
        } else {
            const serviceWorkerContainer = windowService.nativeWindow.navigator.serviceWorker;
            if (serviceWorkerContainer.controller) {
                this.updateCurrentState(ServiceWorkerState.Controlled);
            } else {
                this.updateCurrentState(ServiceWorkerState.SupportedNotControlled);

                serviceWorkerContainer.ready.then(() => {
                    // In cases of Shift+Reload or Ctrl+Reload, the `ready` promise will resolve,
                    // but the `window.navigator.serviceWorker.controller` property will be `null`.
                    // See: https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/controller for details.
                    if (serviceWorkerContainer.controller) {
                        this.updateCurrentState(ServiceWorkerState.Controlled);
                    }
                }).catch(ex => {
                    this.updateCurrentState(ServiceWorkerState.Unsupported);

                    // We don't need to add extra logging for the FireFox security error.
                    // It's a known case where Service Workers are not supported.
                    if (!isFirefoxSecurityError(ex)) {
                        globalErrorHandler.handleError(ex, ErrorId.E5E2n);
                    }
                });
            }
        }
    }

    public get stateObservable(): Observable<ServiceWorkerState> {
        return this.stateSubject.asObservable();
    }

    public get currentState(): ServiceWorkerState {
        return this.currentServiceWorkerState;
    }

    private updateCurrentState(state: ServiceWorkerState) {
        this.currentServiceWorkerState = state;

        if (!this.stateSubject) {
            this.stateSubject = new BehaviorSubject(state);
        } else {
            this.stateSubject.next(state);
        }

        if (state === ServiceWorkerState.Controlled || state === ServiceWorkerState.Unsupported) {
            this.stateSubject.complete();
        }
    }
}
